import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { v4 as uuid } from 'uuid';
import { ConnectionService } from '@services/connection-service';
import { ShiprocketService } from '@services/shiprocket-service';
import { DeliveryDateService } from '@services/delivery-date-service/delivery-date.service';
import { fromActions } from '@store/actions';
import { AppConfig } from 'client/app/app.config';
import { AppWebBridgeService } from '@services/app-web-bridge-service';

export interface DeliveryDateInfo {
  expectedDeliveryDate: { from: Date; to: Date };
  dayDifference?: number;
}

export interface CheckoutParams {
  user: any;
  typeOfOrder: string;
  regimenId: string;
  products?: string;
  messageId?: string;
  services?: string;
  useCureSkinCash?: boolean;
  showRegimenReorderUI?: boolean;
  regimenClass?: string;
  newRegimenPricingExperiment?: any;
}

@Injectable()
export class CheckoutService {
  readonly addressFlowQueryKeysToPreserve = [
    'type',
    'regimenId',
    'useCureSkinCash',
    'products',
    'messageId',
    'services',
  ] as const;

  private readonly appBridgeService: AppWebBridgeService = inject(AppWebBridgeService);
  private readonly store: Store = inject(Store);
  private readonly conn: ConnectionService = inject(ConnectionService);
  private readonly appConfig: AppConfig = inject(AppConfig);
  private readonly shiprocketService: ShiprocketService = inject(ShiprocketService);
  private readonly deliveryDateService: DeliveryDateService = inject(DeliveryDateService);


  sendInitialOrderEvent(): void{
    this.appBridgeService.logEventInBranchAndFirebaseFromiOS({
      branch: { name: 'InitialOrder' },
      firebase: { name: 'InitialOrder' },
    });
  }

  async buildCheckoutPayloadForOrder(params: CheckoutParams): Promise<any> {
    const payload: Record<string, unknown> = {
      userObjectId: params.user?.id,
      paymentType: this.appConfig.Shared.Order.PaymentType.NOT_SELECTED,
      type: params.typeOfOrder,
      regimenId: params.regimenId,
      products: params.products,
      messageId: params.messageId,
      uniqueId: uuid(),
      services: params.services,
    };

    if (params.useCureSkinCash != null) {
      payload.useCureSkinCash = params.useCureSkinCash;
    }

    if (params.showRegimenReorderUI) {
      payload.regimenReorderDetails = { class: params.regimenClass, enabled: true };
    }

    if (params.newRegimenPricingExperiment && params.regimenId) {
      const extraProductIds = params.newRegimenPricingExperiment?.variant?.availableProducts || [];
      await this.conn.updateRegimenPrice({ regimenId: params.regimenId, extraProductIds });
    }

    return payload;
  }

  dispatchCheckoutBegin(payload: any, replaceUrl?: boolean): void {
    this.store.dispatch(fromActions.CheckoutOrderBegin({ payload, replaceUrl }));
  }

  clearShiprocketStateIfNeeded(queryParams: Record<string, any>): void {
    const shiprocket = this.appConfig.Shared.Shiprocket;
    if (queryParams?.source === shiprocket.Source || queryParams?.[shiprocket.PhoneParam]) {
      this.shiprocketService.clearAllAddressData();
      this.shiprocketService.clearLoginToken();
    }
  }

  buildPreservedAddressParams(
    currentQueryParams: Record<string, any>,
    addressParams: Record<string, any>,
  ): Record<string, any> {
    const preservedParams: Record<string, any> = { ...addressParams };

    this.addressFlowQueryKeysToPreserve.forEach((key): void => {
      if (currentQueryParams?.[key] != null && currentQueryParams[key] !== '') {
        preservedParams[key] = currentQueryParams[key];
      }
    });

    return preservedParams;
  }

  navigateToShiprocketAddressList(
    phone: string,
    currentQueryParams: Record<string, any>,
    extraAddressParams: Record<string, any> = {},
  ): void {
    const params = this.buildPreservedAddressParams(currentQueryParams, extraAddressParams);
    params.source = this.appConfig.Shared.Shiprocket.Source;
    params[this.appConfig.Shared.Shiprocket.PhoneParam] = phone;
    this.conn.navigateToURL(this.buildUrlWithParams('/user/address', params));
  }

  buildQueryString(params: Record<string, any>): string {
    return Object.keys(params || {})
      .filter((key: string): boolean => params[key] != null && params[key] !== '')
      .map((key: string): string => `${encodeURIComponent(key)}=${encodeURIComponent(String(params[key]))}`)
      .join('&');
  }

  buildUrlWithParams(base: string, params: Record<string, any>): string {
    const queryString = this.buildQueryString(params);
    return queryString ? `${base}?${queryString}` : base;
  }

  async fetchDeliveryDateInfo(pincode: string, languagePreference: string): Promise<DeliveryDateInfo> {
    const deliveryIntervalInDays = 2;
    const isEnglishOrHindi = [
      this.appConfig.Shared.Languages.EN,
      this.appConfig.Shared.Languages.HI,
    ].includes(languagePreference);

    if (!isEnglishOrHindi) {
      return {
        expectedDeliveryDate: this.deliveryDateService.calculateExpectedDeliveryDate(null, deliveryIntervalInDays),
      };
    }

    try {
      const res = await this.conn.getExpectedDeliveryDateByDestinationPinCode(pincode);
      const responseDateISO = res?.expectedDeliveryDate;
      const todayDate = new Date();
      return {
        dayDifference: this.deliveryDateService.getDeliveryDateDifference(responseDateISO, todayDate),
        expectedDeliveryDate: this.deliveryDateService.calculateExpectedDeliveryDate(responseDateISO, deliveryIntervalInDays),
      };
    } catch {
      return {
        expectedDeliveryDate: this.deliveryDateService.calculateExpectedDeliveryDate(null, deliveryIntervalInDays),
      };
    }
  }
}
